import { Table } from 'antd';
import { TargetRockAnalogueTable } from '../../@types/gheAnalogueTables/types';
import { Card } from '../../design-system';
import styles from './GheTargetAnalogueTable.module.css';

type Props = {
  table: TargetRockAnalogueTable;
};

const formatNumber = (value?: number, decimals = 4) =>
  value == null ? '-' : value.toFixed(decimals);

const numericSorter = (key: string) => (left: any, right: any) =>
  (left?.[key] ?? Number.NEGATIVE_INFINITY) -
  (right?.[key] ?? Number.NEGATIVE_INFINITY);

const columns = [
  {
    title: 'Similarity',
    dataIndex: 'similarity',
    key: 'similarity',
    fixed: 'left' as const,
    sorter: (left: any, right: any) => left.similarity - right.similarity,
    defaultSortOrder: 'descend' as const,
    render: (value: number) => formatNumber(value)
  },
  {
    title: 'Author + Formation',
    dataIndex: 'sample_id',
    key: 'sample_id',
    fixed: 'left' as const
  },
  {
    title: 'Analogous Test for',
    dataIndex: 'analogous_test_for',
    key: 'analogous_test_for'
  },
  {
    title: 'Rock Type',
    dataIndex: 'rock_type',
    key: 'rock_type'
  },
  {
    title: 'Porosity (fraction)',
    dataIndex: 'porosity_fraction',
    key: 'porosity_fraction',
    sorter: numericSorter('porosity_fraction'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Permeability (mD)',
    dataIndex: 'permeability_md',
    key: 'permeability_md',
    sorter: numericSorter('permeability_md'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'RQI',
    dataIndex: 'rqi',
    key: 'rqi',
    sorter: numericSorter('rqi'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'FZI',
    dataIndex: 'fzi',
    key: 'fzi',
    sorter: numericSorter('fzi'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'R35 Winland (microm)',
    dataIndex: 'r35_winland_microm',
    key: 'r35_winland_microm',
    sorter: numericSorter('r35_winland_microm'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'R35 Pitman (microm)',
    dataIndex: 'r35_pitman_microm',
    key: 'r35_pitman_microm',
    sorter: numericSorter('r35_pitman_microm'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'CO2 visc (cP)',
    dataIndex: 'co2_visc_cp',
    key: 'co2_visc_cp',
    sorter: numericSorter('co2_visc_cp'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'CO2 dens (kg/m3)',
    dataIndex: 'co2_density_kg_m3',
    key: 'co2_density_kg_m3',
    sorter: numericSorter('co2_density_kg_m3'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Water visc (cP)',
    dataIndex: 'water_visc_cp',
    key: 'water_visc_cp',
    sorter: numericSorter('water_visc_cp'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Water dens (kg/m3)',
    dataIndex: 'water_density_kg_m3',
    key: 'water_density_kg_m3',
    sorter: numericSorter('water_density_kg_m3'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Viscosity ratio (Brine/CO2)',
    dataIndex: 'viscosity_ratio_brine_co2',
    key: 'viscosity_ratio_brine_co2',
    sorter: numericSorter('viscosity_ratio_brine_co2'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'IFT (mN/m)',
    dataIndex: 'ift_mn_m',
    key: 'ift_mn_m',
    sorter: numericSorter('ift_mn_m'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Capillary Number',
    dataIndex: 'capillary_number',
    key: 'capillary_number',
    sorter: numericSorter('capillary_number'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Rapoport Leas Number (cm2*cP/min)',
    dataIndex: 'rapoport_leas_number',
    key: 'rapoport_leas_number',
    sorter: numericSorter('rapoport_leas_number'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Swi (Dren)',
    dataIndex: 'swi_drainage',
    key: 'swi_drainage',
    sorter: numericSorter('swi_drainage'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Krco2@Swi (Dren)',
    dataIndex: 'krco2_at_swi_drainage',
    key: 'krco2_at_swi_drainage',
    sorter: numericSorter('krco2_at_swi_drainage'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Sco2i (Imb)',
    dataIndex: 'sco2i_imbibition',
    key: 'sco2i_imbibition',
    sorter: numericSorter('sco2i_imbibition'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Krw@Sco2i (Imb)',
    dataIndex: 'krw_at_sco2i_imbibition',
    key: 'krw_at_sco2i_imbibition',
    sorter: numericSorter('krw_at_sco2i_imbibition'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Swi_Pc',
    dataIndex: 'swi_pc',
    key: 'swi_pc',
    sorter: numericSorter('swi_pc'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Pe (KPa)',
    dataIndex: 'pe_kpa',
    key: 'pe_kpa',
    sorter: numericSorter('pe_kpa'),
    render: (value?: number) => formatNumber(value)
  },
  {
    title: 'Landa_BC',
    dataIndex: 'lambda_bc',
    key: 'lambda_bc',
    sorter: numericSorter('lambda_bc'),
    render: (value?: number) => formatNumber(value)
  }
];

export const GheTargetAnalogueTable = ({ table }: Props) => {
  return (
    <Card
      variant="default"
      padding="none"
      fullWidth
      style={{ marginBottom: '1rem' }}
      data-testid={`ghe-analogue-table-${table.rock_id}`}
    >
      <Card.Header title={table.rock_id} />
      <Card.Body style={{ padding: 0 }}>
        <Table
          className={styles.table}
          rowKey={row => `${table.rock_id}-${row.sample_id}-${row.analogous_test_for}`}
          rowClassName={(_, index) => (index % 2 === 1 ? styles.stripedRow : '')}
          pagination={false}
          dataSource={table.rows}
          columns={columns}
          scroll={{ x: 'max-content' }}
        />
      </Card.Body>
    </Card>
  );
};
